import type { ListingResearch } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { BookmarkPlus } from "lucide-react";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { describeError } from "../../lib/errors";
import { RESEARCH_STATUS_LABELS, RESEARCH_STATUS_TONES } from "./format";

interface ResearchCellProps {
  readonly listingTitle: string;
  /** The listing's open research record, if it has one. */
  readonly saved: ListingResearch | undefined;
  readonly currentUserId: string;
  /** The card that was searched for; null after a typed-words search. */
  readonly searchedCardId: string | null;
  readonly saving: boolean;
  readonly error: unknown;
  readonly onSave: () => void;
}

/** A search result's Research column: "Save to Research", or who already has it in Research and how far it is. */
export function ResearchCell({
  listingTitle,
  saved,
  currentUserId,
  searchedCardId,
  saving,
  error,
  onSave,
}: ResearchCellProps) {
  if (saved !== undefined) {
    return (
      <div className="flex flex-col items-start gap-1">
        <Badge tone={RESEARCH_STATUS_TONES[saved.status]}>
          In Research · {RESEARCH_STATUS_LABELS[saved.status]}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {saved.owner.id === currentUserId ? "Saved by you" : `Saved by ${saved.owner.name}`}
        </span>
        {saved.card.id !== searchedCardId && (
          <span className="text-xs text-muted-foreground">
            As {saved.card.name} {saved.card.number}
          </span>
        )}
        <Link
          to="/research/$researchId"
          params={{ researchId: saved.researchId }}
          aria-label={`Open research: ${listingTitle}`}
          className="text-xs font-medium text-primary hover:underline"
        >
          Open
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant="secondary"
        size="sm"
        onClick={onSave}
        disabled={saving}
        aria-label={`Save to Research: ${listingTitle}`}
      >
        <BookmarkPlus className="size-3.5" aria-hidden="true" />
        {saving ? "Saving…" : "Save to Research"}
      </Button>
      {error !== null && error !== undefined && (
        <p role="alert" className="text-xs text-destructive">
          {describeError(error).message}
        </p>
      )}
    </div>
  );
}
